import {
  type ContentCatalog,
  ContentCatalogSchema,
  catalogLevels,
  DRAFTS_QUERY_PARAM,
  type Level,
  LevelResponseSchema,
} from '@md/shared';

/**
 * Bibliothèque de contenu du client (spec §6/§7).
 *
 * Depuis la phase 4, le jeu ne connaît plus les niveaux par des fichiers
 * importés à la compilation : il lit le catalogue servi par `/api/content` et
 * charge le JSON d'un niveau par `/api/levels/:id`. Les niveaux du dépôt sont
 * devenus de simples **graines** réimportées en base au démarrage du serveur.
 *
 * Toutes les réponses passent par zod avant d'atteindre le moteur.
 */
export interface LevelLibrary {
  /** Charge le catalogue puis, en tâche de fond, le JSON de chaque niveau. */
  load(): Promise<ContentCatalog>;
  catalog(): ContentCatalog | null;
  /** Niveau déjà chargé, sans aller-retour réseau. */
  get(levelId: string): Level | undefined;
  /** Niveau chargé, en le récupérant au besoin. */
  ensure(levelId: string): Promise<Level | undefined>;
  /** Identifiant du premier niveau du catalogue. */
  firstLevelId(): string | undefined;
  /** Le catalogue inclut-il les brouillons (mode test) ? */
  includesDrafts(): boolean;
  lastError(): string | null;
  /** Notifié à chaque changement de catalogue. */
  subscribe(listener: (catalog: ContentCatalog) => void): () => void;
}

export interface LevelLibraryOptions {
  /** Préfixe des URL d'API (vide = même origine). */
  readonly baseUrl?: string;
  /** Jeton Bearer donnant accès aux brouillons (`?drafts=<token>`). */
  readonly token?: string | null;
  readonly gameId: string;
  /** Injectable pour les tests. */
  readonly fetch?: typeof globalThis.fetch;
}

export function createLevelLibrary(options: LevelLibraryOptions): LevelLibrary {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const base = options.baseUrl ?? '';
  const token = options.token ?? null;
  const levels = new Map<string, Level>();
  const pending = new Map<string, Promise<Level | undefined>>();
  const listeners = new Set<(catalog: ContentCatalog) => void>();
  let current: ContentCatalog | null = null;
  let error: string | null = null;

  const headers = (): HeadersInit => (token === null ? {} : { Authorization: `Bearer ${token}` });

  const withDrafts = (url: string): string =>
    token === null ? url : `${url}${url.includes('?') ? '&' : '?'}${DRAFTS_QUERY_PARAM}=1`;

  const notify = (catalog: ContentCatalog): void => {
    for (const listener of listeners) {
      listener(catalog);
    }
  };

  const fetchLevel = async (levelId: string): Promise<Level | undefined> => {
    const response = await doFetch(
      withDrafts(`${base}/api/levels/${encodeURIComponent(levelId)}`),
      { headers: headers() },
    );
    if (!response.ok) {
      return undefined;
    }
    const parsed = LevelResponseSchema.safeParse(await response.json());
    if (!parsed.success) {
      return undefined;
    }
    levels.set(levelId, parsed.data.level);
    return parsed.data.level;
  };

  const library: LevelLibrary = {
    async load(): Promise<ContentCatalog> {
      const response = await doFetch(
        withDrafts(`${base}/api/content?game=${encodeURIComponent(options.gameId)}`),
        { headers: headers() },
      );
      if (!response.ok) {
        error = `l'API de contenu a répondu ${response.status}`;
        throw new Error(error);
      }
      const parsed = ContentCatalogSchema.parse(await response.json());
      current = parsed;
      error = null;
      notify(parsed);
      // Préchargement : le catalogue est petit, et un niveau déjà en mémoire
      // évite toute latence au moment du décompte 3-2-1.
      for (const summary of catalogLevels(parsed)) {
        if (!levels.has(summary.id)) {
          void library.ensure(summary.id);
        }
      }
      return parsed;
    },

    catalog(): ContentCatalog | null {
      return current;
    },

    get(levelId: string): Level | undefined {
      return levels.get(levelId);
    },

    async ensure(levelId: string): Promise<Level | undefined> {
      const known = levels.get(levelId);
      if (known) {
        return known;
      }
      const inflight = pending.get(levelId);
      if (inflight) {
        return inflight;
      }
      const request = fetchLevel(levelId).finally(() => pending.delete(levelId));
      pending.set(levelId, request);
      return request;
    },

    firstLevelId(): string | undefined {
      return current ? catalogLevels(current)[0]?.id : undefined;
    },

    includesDrafts(): boolean {
      return current?.includesDrafts ?? false;
    },

    lastError(): string | null {
      return error;
    },

    subscribe(listener): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };

  return library;
}
